<template>
  <div v-if="localCustomer">
    <!-- Basic Information Card -->
    <div class="card">
      <div class="card-header bg-light" v-if="showPersonalDetailsHeader">
        <h5 class="card-title mb-0">{{ basicDetailsCardTitle }}</h5>
      </div>
      <div class="card-body">
        <form data-toggle="validator" role="form">
          <!-- Basic details section -->
          <BasicDetailsSection
            :value="localCustomer"
            :show-id-number="showIdNumber"
            :show-gender="showGender"
            :show-date-of-birth="showDateOfBirth"
            :show-relationship-type="showRelationshipType"
            :relationship-types="filteredRelationshipTypes"
            :selected-relationship="selectedRelationshipType"
            @input="updateBasicDetails"
            @relationship-change="updateRelationshipType"
          />
        </form>
      </div>
    </div>

    <!-- Contact Information Card -->
    <div class="card" v-if="showContactPhones || showContactEmails || showContactAddresses">
      <div class="card-header bg-light">
        <h5 class="card-title mb-0">Contacts</h5>
      </div>
      <div class="card-body">
        <form data-toggle="validator" role="form">
          <div>
            <!-- Phone Numbers Section -->
            <PhoneNumbersSection
              v-if="showContactPhones"
              :value="localCustomer.phones"
              :country-codes="countryCodes"
              :max-items="maxPhones"
              @input="updatePhones"
            />

            <!-- Email Addresses Section -->
            <EmailAddressesSection
              v-if="showContactEmails"
              :value="localCustomer.emails"
              :max-items="maxEmails"
              @input="updateEmails"
            />

            <!-- Addresses Section -->
            <AddressesSection
              v-if="showContactAddresses"
              :value="localCustomer.addresses"
              :countries="countries"
              :address-types="addressTypes"
              :max-items="maxAddresses"
              @input="updateAddresses"
            />
          </div>
        </form>
      </div>
    </div>

    <!-- File Attachments Card -->
    <div class="card">
      <div class="card-header bg-light">
        <h5 class="card-title mb-0">Attachments</h5>
      </div>
      <div class="card-body">
        <button 
          type="button" 
          class="add-document-btn" 
          @click="openFileMetadataModal"
          :disabled="isUploadingFile"
        >
          <i v-if="isUploadingFile" class="fa fa-spinner fa-spin mr-2"></i>
          <i v-else class="fa fa-plus mr-2"></i>
          {{ isUploadingFile ? 'Uploading...' : 'Add Document' }}
        </button>
        <section class="uploaded-area" v-if="activeFiles.length">
          <li class="row-info" v-for="(file, index) in activeFiles" :key="index">
            <div class="content upload">
              <i class="fas fa-file-alt icon"></i>
              <div class="details">
                <span class="name">
                  <a href="javascript:void(0);"
                     @click="viewFile(file.fileId, file.contentType)">{{ file.name }}</a>
                </span>
                <span class="size">{{ formatFileSize(file.size) }}</span>
                <span v-if="file.documentType" class="file-type">
                  <i class="fa fa-tag mr-1"></i>{{ getFileTypeLabel(file.documentType) }}
                </span>
                <span v-if="file.description" class="file-description">{{ file.description }}</span>
              </div>
            </div>
            <div class="actions">
              <button
                @click="deleteFile(file.fileId)"
                class="btn btn-sm text-red"
              >
                <i class="fa fa-trash mr-1 action-icons" style="color: orangered"></i>
              </button>
            </div>
          </li>
        </section>
      </div>
    </div>

    <!-- File Metadata Modal -->
    <div v-if="showFileMetadataModal" class="modal-overlay" @click.self="cancelFileUpload">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Add Document</h5>
          <button type="button" class="close-btn" @click="cancelFileUpload">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group mb-3">
            <label class="form-label required">Document Type</label>
            <select v-model="currentFileMetadata.documentType" class="form-control" required>
              <option value="">Select document type</option>
              <option v-for="type in fileTypes" :key="type.value" :value="type.value">{{ type.label }}</option>
            </select>
          </div>

          <div class="form-group mb-3">
            <label class="form-label">Description</label>
            <textarea 
              v-model="currentFileMetadata.description" 
              class="form-control" 
              rows="3"
              placeholder="Enter description (optional)"
            ></textarea>
          </div>

          <div class="form-group mb-3">
            <label class="form-label required">Select File</label>
            <input
              type="file"
              ref="modalFileInput"
              class="form-control"
              @change="handleFileSelection"
              :disabled="!currentFileMetadata.documentType"
            />
            <small class="form-text text-muted" v-if="!currentFileMetadata.documentType">
              Please select a document type first
            </small>
          </div>

          <div v-if="selectedFileName" class="selected-file-display">
            <i class="fa fa-file mr-2"></i>
            <strong>{{ selectedFileName }}</strong>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="cancelFileUpload">
            Cancel
          </button>
          <button 
            type="button" 
            class="btn btn-primary" 
            @click="uploadSelectedFile"
            :disabled="!currentFileMetadata.documentType || !selectedFile || isUploadingFile"
          >
            <i v-if="isUploadingFile" class="fa fa-spinner fa-spin mr-1"></i>
            {{ isUploadingFile ? 'Uploading...' : 'Upload' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Floating Save Button -->
    <button 
      type="button"
      class="floating-save-btn" 
      :disabled="isSaving"
      @click="saveChanges">
      <i v-if="isSaving" class="fa fa-spinner fa-spin mr-1"></i>
      <i v-else class="fa fa-save mr-1"></i>
      {{ isSaving ? 'Saving...' : 'Save Changes' }}
    </button>
  </div>
</template>

<script>
import DatePicker from "vue2-datepicker";
import Multiselect from "vue-multiselect";
import {handleError, showSuccessToast, showWarning} from "../../utils/notification.utils";
import permissionService from "../../service/permission.service";
import contactService from "../../service/contact.service";
import fileService from "../../service/file.service";
import BasicDetailsSection from "./sections/BasicDetailsSection.vue";
import PhoneNumbersSection from "./sections/PhoneNumbersSection.vue";
import EmailAddressesSection from "./sections/EmailAddressesSection.vue";
import AddressesSection from "./sections/AddressesSection.vue";
import MetadataSection from "./sections/MetadataSection.vue";
import {relationshipTypes} from "../../utils/relationship.types";
import {errorCodes} from "../../interceptors/codes";

export default {
  components: {
    DatePicker,
    Multiselect,
    BasicDetailsSection,
    PhoneNumbersSection,
    EmailAddressesSection,
    AddressesSection,
    MetadataSection
  },
  data() {
    return {
      permissions: {},
      genders: ["MALE", "FEMALE"],
      selectedGenderId: "",
      pagePermissions: ["CUSTOMER_READ", "CUSTOMER_DELETE"],
      countryCodes: [
        {value: "1", label: "+1"},
        {value: "44", label: "+44"},
        {value: "263", label: "+263"},
        {value: "27", label: "+27"},
        {value: "254", label: "+254"},
        {value: "234", label: "+234"},
        {value: "61", label: "+61"},
        {value: "91", label: "+91"}
      ],
      addressTypes: ["RESIDENTIAL", "BILLING", "SHIPPING", "OTHER"],
      countries: [
        {value: "US", label: "United States"},
        {value: "CA", label: "Canada"},
        {value: "GB", label: "United Kingdom"},
        {value: "AU", label: "Australia"},
        {value: "ZW", label: "Zimbabwe"},
        {value: "ZA", label: "South Africa"},
        {value: "KE", label: "Kenya"},
        {value: "NG", label: "Nigeria"},
        {value: "IN", label: "India"}
      ],
      localCustomer: {
        firstName: null,
        lastName: null,
        idNumber: null,
        emails: [{
          emailAddress: "",
          emailType: "PRIMARY"
        }],
        phones: [{
          countryCode: "263",
          phoneNumber: "",
          phoneType: "MOBILE"
        }],
        addresses: [{
          addressLine1: "",
          addressLine2: "",
          city: "",
          state: "",
          postalCode: "",
          country: "US",
          addressType: "RESIDENTIAL"
        }],
        metadata: [{
          key: "",
          value: ""
        }],
        gender: null,
        dateOfBirth: null,
        externalId: null,
      },
      isDataInitialized: false,  // Add this flag
      selectedRelationshipType: null,
      filteredRelationshipTypes: [],
      files: [],
      isUploadingFile: false,
      isSaving: false,
      showFileMetadataModal: false,
      selectedFile: null,
      selectedFileName: '',
      fileTypes: [
        { value: 'PASSPORT', label: 'Passport' },
        { value: 'DRIVERS_LICENSE', label: "Driver's License" },
        { value: 'NATIONAL_ID', label: 'National ID' },
        { value: 'BIRTH_CERTIFICATE', label: 'Birth Certificate' },
        { value: 'PROOF_OF_RESIDENCE', label: 'Proof of Residence' },
        { value: 'UTILITY_BILL', label: 'Utility Bill' },
        { value: 'BANK_STATEMENT', label: 'Bank Statement' },
        { value: 'CONTRACT', label: 'Contract' },
        { value: 'LEASE_AGREEMENT', label: 'Lease Agreement' },
        { value: 'INVOICE', label: 'Invoice' },
        { value: 'RECEIPT', label: 'Receipt' },
        { value: 'MEDICAL_RECORD', label: 'Medical Record' },
        { value: 'INSURANCE_DOCUMENT', label: 'Insurance Document' },
        { value: 'POLICE_CLEARANCE', label: 'Police Clearance' },
        { value: 'PHOTO', label: 'Photo' },
        { value: 'OTHER', label: 'Other' }
      ],
      currentFileMetadata: {
        documentType: '',
        description: ''
      }
    };
  },
  props: {
    customer: {
      type: Object,
      required: true
    },
    showRelationshipType: {
      type: Boolean,
      default: false
    },
    showPersonalDetailsHeader: {
      type: Boolean,
      default: true
    },
    basicDetailsCardTitle: {
      type: String,
      default: "Basic Details"
    },
    showContactPhones: {
      type: Boolean,
      default: true
    },
    showContactEmails: {
      type: Boolean,
      default: true
    },
    showContactAddresses: {
      type: Boolean,
      default: true
    },
    showIdNumber: {
      type: Boolean,
      default: false
    },
    showGender: {
      type: Boolean,
      default: false
    },
    showDateOfBirth: {
      type: Boolean,
      default: false
    },
    useDirectEndpoint: {
      type: Boolean,
      default: false
    },
    showMetadata: {
      type: Boolean,
      default: true
    },
    maxPhones: {
      type: Number,
      default: null
    },
    maxEmails: {
      type: Number,
      default: null
    },
    maxAddresses: {
      type: Number,
      default: null
    }
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    currentStore() {
      return localStorage.getItem("mid");
    },
    activeFiles() {
      return this.files.filter(file => !file.deleted);
    }
  },
  methods: {
    getFileTypeLabel(typeValue) {
      const fileType = this.fileTypes.find(ft => ft.value === typeValue);
      return fileType ? fileType.label : typeValue;
    },
    
    initializeLocalData() {
      // Prevent multiple initializations
      if (this.isDataInitialized && this.customer.id) return;

      // Copy customer data to local state for editing
      // Create a clean copy without references to the original
      const customerCopy = JSON.parse(JSON.stringify(this.customer));

      this.localCustomer = {
        ...customerCopy,
        firstName: customerCopy.firstName || null,
        lastName: customerCopy.lastName || null,
        idNumber: customerCopy.idNumber || null,
        gender: customerCopy.gender || "",
        externalId: customerCopy.externalId || null,
      };

      this.selectedGenderId = customerCopy.gender || "";

      // Handle phone numbers conversion
      if (customerCopy.phone && (!customerCopy.phones || customerCopy.phones.length === 0)) {
        this.$set(this.localCustomer, 'phones', [{
          countryCode: "263", // Default country code
          phoneNumber: customerCopy.phone,
          phoneType: "MOBILE" // Default type
        }]);
      } else if (!this.localCustomer.phones || !this.localCustomer.phones.length) {
        this.$set(this.localCustomer, 'phones', [{
          countryCode: "263",
          phoneNumber: "",
          phoneType: "MOBILE"
        }]);
      }

      // Handle email conversion
      if (customerCopy.email && (!customerCopy.emails || customerCopy.emails.length === 0)) {
        this.$set(this.localCustomer, 'emails', [{
          emailAddress: customerCopy.email,
          emailType: "PRIMARY"
        }]);
      } else if (!this.localCustomer.emails || !this.localCustomer.emails.length) {
        this.$set(this.localCustomer, 'emails', [{
          emailAddress: "",
          emailType: "PRIMARY"
        }]);
      }

      // Handle addresses
      if (!this.localCustomer.addresses || !this.localCustomer.addresses.length) {
        this.$set(this.localCustomer, 'addresses', [{
          addressLine1: "",
          addressLine2: "",
          city: "",
          state: "",
          postalCode: "",
          country: "US",
          addressType: "RESIDENTIAL"
        }]);
      }

      // Handle metadata
      if (customerCopy.metadata && typeof customerCopy.metadata === 'object' && !Array.isArray(customerCopy.metadata)) {
        const metadataArray = Object.entries(customerCopy.metadata).map(([key, value]) => ({
          key,
          value: String(value)
        }));
        this.$set(this.localCustomer, 'metadata', metadataArray.length > 0 ? metadataArray : [{key: "", value: ""}]);
      } else if (!this.localCustomer.metadata || this.localCustomer.metadata.length === 0) {
        this.$set(this.localCustomer, 'metadata', [{key: "", value: ""}]);
      }

      // Handle attachments
      if (customerCopy.attachments && Array.isArray(customerCopy.attachments)) {
        this.files = customerCopy.attachments.map(attachment => ({
          id: attachment.id,
          fileId: attachment.id,
          name: attachment.fileName,
          size: attachment.fileSize,
          contentType: attachment.contentType,
          documentType: attachment.documentType || null,
          description: attachment.description || null,
          deleted: false
        }));
      } else {
        this.files = [];
      }

      this.isDataInitialized = true;
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    openFileMetadataModal() {
      this.showFileMetadataModal = true;
      this.currentFileMetadata = {
        documentType: '',
        description: ''
      };
      this.selectedFile = null;
      this.selectedFileName = '';
    },

    handleFileSelection(event) {
      if (event.target.files && event.target.files.length > 0) {
        this.selectedFile = event.target.files[0];
        this.selectedFileName = this.selectedFile.name;
      }
    },
    
    async uploadSelectedFile() {
      if (!this.currentFileMetadata.documentType) {
        showWarning('Please select a document type');
        return;
      }

      if (!this.selectedFile) {
        showWarning('Please select a file');
        return;
      }

      this.isUploadingFile = true;

      try {
        const customerId = localStorage.getItem('contextId') || localStorage.getItem('selectedContextId');
        const response = await fileService.uploadFileForCustomer(customerId, this.selectedFile);
        
        if (response.data.code === 'SUCCESS') {
          const fileDto = response.data.data;
          this.files.push({
            id: fileDto.id,
            fileId: fileDto.id,
            name: fileDto.fileName,
            size: fileDto.fileSize,
            contentType: fileDto.contentType,
            documentType: this.currentFileMetadata.documentType,
            description: this.currentFileMetadata.description || null,
            deleted: false
          });

          this.closeFileMetadataModal();
        }
      } catch (e) {
        console.log(e);
        const errorMessage = (e.response && e.response.data && (e.response.data.narrative || e.response.data.description)) || 'Failed to upload file';
        showWarning(errorMessage);
      } finally {
        this.isUploadingFile = false;
      }
    },

    cancelFileUpload() {
      this.closeFileMetadataModal();
    },

    closeFileMetadataModal() {
      this.showFileMetadataModal = false;
      this.selectedFile = null;
      this.selectedFileName = '';
      this.currentFileMetadata = {
        documentType: '',
        description: ''
      };
      if (this.$refs.modalFileInput) {
        this.$refs.modalFileInput.value = '';
      }
    },
    viewFile(fileId) {
      const customerId = localStorage.getItem('contextId') || localStorage.getItem('selectedContextId');
      fileService.getFileUrlForCustomer(customerId, fileId)
        .then(response => {
          let success = response.data.code === 'SUCCESS';
          if (success) {
            const fileDto = response.data.data;
            window.open(fileDto.fileUrl, '_blank')
          }
        })
        .catch(e => {
          console.log(e);
          const errorMessage = (e.response && e.response.data && (e.response.data.narrative || e.response.data.description)) || 'Failed to view file';
          showWarning(errorMessage);
        });
    },
    deleteFile(fileId) {
      // Mark file as deleted instead of removing it immediately
      const file = this.files.find(f => f.fileId === fileId);
      if (file) {
        file.deleted = true;
      }
    },
    saveChanges() {
      if (this.isSaving) return;

      // Filter out any empty phone entries
      const phones = this.localCustomer.phones.filter(phone => phone.phoneNumber.trim() !== '');
      // Filter out any empty email entries
      const emails = this.localCustomer.emails.filter(email => email.emailAddress.trim() !== '');
      // Filter out any empty address entries (checking addressLine1)
      const addresses = this.localCustomer.addresses.filter(addr => addr.addressLine1 && addr.addressLine1.trim() !== '');

      // Convert metadata array to a map
      const metadata = this.localCustomer.metadata
        .filter(meta => meta.key && meta.key.trim() !== "")
        .reduce((obj, item) => {
          obj[item.key] = item.value;
          return obj;
        }, {});

      // Prepare attachments with id, fileId, type, description, and deleted flag
      const attachments = this.files.map(file => ({
        id: file.id,
        fileId: file.fileId,
        documentType: file.documentType || null,
        description: file.description || null,
        deleted: file.deleted || false
      }));

      const data = {
        firstName: this.localCustomer.firstName,
        lastName: this.localCustomer.lastName,
        idNumber: this.localCustomer.idNumber,
        gender: this.localCustomer.gender ? this.localCustomer.gender.toUpperCase() : null,
        dateOfBirth: this.localCustomer.dateOfBirth ? new Date(this.localCustomer.dateOfBirth).toISOString() : null,
        emails: emails.length > 0 ? emails : null,
        phones: phones.length > 0 ? phones : null,
        addresses: addresses.length > 0 ? addresses : null,
        metadata: Object.keys(metadata).length > 0 ? metadata : null,
        externalId: this.localCustomer.externalId,
        businessId: localStorage.getItem("mid"),
        companyId: localStorage.getItem("companyId"),
        type: this.showRelationshipType && this.selectedRelationshipType ? this.selectedRelationshipType.id : null,
        attachments: attachments
      };

      this.isSaving = true;

      // If customer has an ID, update it, otherwise create a new one
      if (this.customer.id) {
        this.updateContact(data);
      } else {
        this.createContact(data);
      }
    },

    updateContact(data) {
      const contextId = localStorage.getItem('contextId') || localStorage.getItem('selectedContextId');
      const updatePromise = this.useDirectEndpoint
        ? contactService.updateDirect(this.customer.id, data)
        : contactService.update(contextId, this.customer.id, data);

      updatePromise
        .then(response => {
          if (response.data.code === 'SUCCESS') {
            this.$emit('customer-updated', response.data.data);
            showSuccessToast("Saved successfully!");
          } else {
            showWarning(response.data.narrative);
          }
        })
        .catch(handleError)
        .finally(() => {
          this.isSaving = false;
        });
    },

    createContact(data) {
      const contextId = localStorage.getItem('contextId') || localStorage.getItem('selectedContextId');
      contactService.create(contextId, data)
        .then(response => {
          if (response.data.code === 'SUCCESS') {
            this.$emit('customer-updated', response.data.data);
            showSuccessToast("Saved successfully!");
          } else {
            showWarning(response.data.narrative || "Failed to create contact");
          }
        })
        .catch(handleError)
        .finally(() => {
          this.isSaving = false;
        });
    },

    // Add these new methods to update specific parts of the localCustomer object
    updateBasicDetails(data) {
      // Make sure we're not overwriting other sections
      const updatedCustomer = {...this.localCustomer};

      // Update only the basic fields
      Object.keys(data).forEach(key => {
        if (key !== 'phones' && key !== 'emails' && key !== 'addresses' && key !== 'metadata') {
          updatedCustomer[key] = data[key];
        }
      });

      // Update the entire object at once to maintain reactivity
      this.localCustomer = updatedCustomer;
    },

    updatePhones(phones) {
      // Create a deep copy of the current state
      const updatedCustomer = {...this.localCustomer};
      // Update only the phones array
      updatedCustomer.phones = JSON.parse(JSON.stringify(phones));
      // Replace the entire object to ensure reactivity
      this.localCustomer = updatedCustomer;
    },

    updateEmails(emails) {
      const updatedCustomer = {...this.localCustomer};
      updatedCustomer.emails = JSON.parse(JSON.stringify(emails));
      this.localCustomer = updatedCustomer;
    },

    updateAddresses(addresses) {
      const updatedCustomer = {...this.localCustomer};
      updatedCustomer.addresses = JSON.parse(JSON.stringify(addresses));
      this.localCustomer = updatedCustomer;
    },

    updateMetadata(metadata) {
      const updatedCustomer = {...this.localCustomer};
      updatedCustomer.metadata = JSON.parse(JSON.stringify(metadata));
      this.localCustomer = updatedCustomer;
    },

    updateRelationshipType(relationshipType) {
      this.selectedRelationshipType = relationshipType;
    },
  },
  watch: {
    customer: {
      immediate: true,
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          // Only initialize if we have new data or haven't initialized yet
          if (!this.isDataInitialized || newVal.id !== this.localCustomer.id) {
            this.isDataInitialized = false; // Reset flag for new customer
            this.initializeLocalData();
          }
        }
      },
      deep: false // Change to false to prevent unnecessary deep watching
    }
  },
  async mounted() {
    if (!this.currentUser) {
      await this.$router.push("/login");
      return;
    }

    const result = await permissionService.checkPermissions(this.pagePermissions);
    if (!result.success) {
      await showWarning('Error during permissions check')
      return;
    }

    this.permissions = result.data;

    if (this.customer && Object.keys(this.customer).length > 0) {
      this.initializeLocalData();
    }

    // Initialize relationship types if needed
    if (this.showRelationshipType) {
      this.filteredRelationshipTypes = relationshipTypes.filter(relationshipType => 
        relationshipType.industries.includes(this.$i18n.locale)
      );
      
      // If customer has a type, use it, otherwise default to first option
      if (this.customer && this.customer.type) {
        this.selectedRelationshipType = this.filteredRelationshipTypes.find(
          type => type.id === this.customer.type
        );
      } else if (this.filteredRelationshipTypes.length > 0) {
        this.selectedRelationshipType = this.filteredRelationshipTypes[0];
      }
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.phone-entry {
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 10px;
  background-color: #f9f9f9;
}

/* Card Styles */
.card {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: none;
  margin-bottom: 1.5rem;
}

.card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.card-header.bg-light {
  background-color: #f8f9fa !important;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.25rem;
}

/* Floating Save Button */
.floating-save-btn {
  position: fixed;
  bottom: 30px;
  right: 30px;
  height: 56px;
  padding: 0 24px;
  border-radius: 28px;
  background-color: #65cf21;
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(101, 207, 33, 0.4);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s ease;
  z-index: 1000;
  white-space: nowrap;
}

.floating-save-btn:hover:not(:disabled) {
  background-color: #52a91a;
  box-shadow: 0 6px 16px rgba(101, 207, 33, 0.6);
  transform: translateY(-2px);
}

.floating-save-btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(101, 207, 33, 0.4);
}

.floating-save-btn:disabled {
  background-color: #65cf21;
  cursor: not-allowed;
  opacity: 0.5;
  box-shadow: 0 2px 8px rgba(101, 207, 33, 0.3);
}

.floating-save-btn i {
  font-size: 18px;
}

/* File Attachment Styles */
.add-document-btn {
  width: 100%;
  padding: 1rem;
  border: 2px dashed #1aaa4c;
  border-radius: 8px;
  background: white;
  color: #1aaa4c;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1rem;
}

.add-document-btn:hover:not(:disabled) {
  background: #f8fff9;
  border-color: #0d8a3a;
}

.add-document-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.uploaded-area {
  max-height: 200px;
  overflow-y: auto;
}

.uploaded-area::-webkit-scrollbar {
  width: 6px;
}

.uploaded-area::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.uploaded-area::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.uploaded-area::-webkit-scrollbar-thumb:hover {
  background: #555;
}

section .row-info {
  background: #d0ffd3;
  border-radius: 5px;
  margin: 0px 0px 10px;
  display: flex;
  list-style: none;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
}

section .row-info .icon {
  color: #1aaa4c;
  font-size: 30px;
}

section .row-info .content {
  display: flex;
  align-items: center;
  flex-direction: row;
}

section .row-info .details {
  display: flex;
  margin-left: 15px;
  flex-direction: column;
}

section .row-info .details .name a {
  color: #333;
  text-decoration: none;
}

section .row-info .details .name a:hover {
  text-decoration: underline;
}

section .row-info .details .size {
  color: #404040;
  font-size: 11px;
}

section .row-info .details .file-type {
  color: #1aaa4c;
  font-size: 12px;
  font-weight: 500;
  margin-top: 2px;
  display: block;
}

section .row-info .details .file-description {
  color: #666;
  font-size: 11px;
  font-style: italic;
  margin-top: 2px;
  display: block;
}

section .row-info .actions .action-icons {
  color: orangered;
  font-size: 18px;
  transition: color 0.3s ease;
  cursor: pointer;
}

section .row-info .actions .action-icons:hover {
  color: red;
}

/* Modern form styles matching CreateReceipt */
.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #495057;
}

.form-label.required::after {
  content: " *";
  color: #dc3545;
}

.form-control {
  border-radius: 0.25rem;
  border: 1px solid #ced4da;
  padding: 0.375rem 0.75rem;
}

.form-control:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* File Metadata Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 500;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #000;
}

.modal-body {
  padding: 1.5rem;
}

.selected-file-display {
  background: #e7f7ed;
  padding: 0.75rem;
  border-radius: 4px;
  word-break: break-word;
  border: 1px solid #1aaa4c;
  color: #0d8a3a;
  margin-top: 0.5rem;
}

.file-counter {
  text-align: center;
  color: #6c757d;
  font-size: 0.875rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  border: 1px solid transparent;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-primary {
  background: #65cf21;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #52a91a;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>



// WEBPACK FOOTER //
// src/components/ContactDetails/ContactDetailsForm.vue